import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Bar,
  BarChart,
  CartesianGrid,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from 'recharts';
import {
  AlertTriangle,
  Download,
  Globe,
  HandCoins,
  Layers,
  Receipt,
  Settings,
  UserMinus,
  Users,
  Wallet,
} from 'lucide-react';
import {
  ajoApi,
  AjoAdminGroup,
  AjoFeeConfig,
  AjoGroupHealth,
  AjoCoverPoolSummary,
  AjoListingForReview,
  AjoRatingRow,
  AjoListingReportRow,
  AjoMemberExitRequest,
  AjoReferralStats,
} from '../../api/ajo';
import { isApprovalRequired } from '../../api/approvals';
import { AjoWalletTab } from './AjoWalletTab';
import { Badge } from '../../components/ui/Badge';
import { Button } from '../../components/ui/Button';
import { Card } from '../../components/ui/Card';
import { Spinner } from '../../components/ui/Spinner';
import { StatCard } from '../../components/ui/StatCard';
import { useAuth } from '../../hooks/useAuth';
import { usePage } from '../../hooks/usePage';
import { useToast } from '../../hooks/useToast';

// Ajo has no plans or subscriptions: every feature is open to everybody.
type Tab = 'groups' | 'wallet' | 'exits' | 'listings' | 'fees' | 'risk' | 'ops';

const tabs: { key: Tab; label: string; icon: React.ElementType }[] = [
  { key: 'groups', label: 'Ajo Groups', icon: Users },
  { key: 'wallet', label: 'Ajo Wallet', icon: Wallet },
  { key: 'exits', label: 'Member Exits', icon: UserMinus },
  { key: 'listings', label: 'Public Listings', icon: Globe },
  { key: 'fees', label: 'Ajo Fees', icon: Receipt },
  { key: 'risk', label: 'Risk & Health', icon: AlertTriangle },
  { key: 'ops', label: 'Operations', icon: Settings },
];

const inputStyle: React.CSSProperties = {
  height: 38,
  padding: '0 10px',
  border: '1px solid var(--neutral-300)',
  borderRadius: 'var(--radius-md)',
  color: 'var(--neutral-800)',
  background: 'var(--neutral-100)',
  fontFamily: 'var(--font-family)',
  fontSize: 14,
  outline: 'none',
  width: '100%',
};

const formatMoney = (value: number) =>
  `₦${Number(value || 0).toLocaleString('en-NG', {
    minimumFractionDigits: 0,
    maximumFractionDigits: 2,
  })}`;

const formatDate = (iso: string | null | undefined) => {
  if (!iso) return '—';
  return new Date(iso).toLocaleDateString('en-NG', {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
  });
};

const titleFromKey = (key: string) =>
  key
    .split('_')
    .map((part) => part.charAt(0).toUpperCase() + part.slice(1))
    .join(' ');

const badgeVariant = (status: string): 'success' | 'warning' | 'danger' | 'neutral' => {
  if (status === 'active' || status === 'completed') return 'success';
  if (status === 'inviting' || status === 'draft') return 'warning';
  if (status === 'cancelled' || status === 'archived') return 'danger';
  return 'neutral';
};

export const Ajo: React.FC = () => {
  usePage('Ajo');
  const { admin } = useAuth();
  const toast = useToast();
  const canEdit = admin?.role === 'super_admin' || admin?.role === 'finance';

  const [activeTab, setActiveTab] = useState<Tab>('groups');
  const [fees, setFees] = useState<AjoFeeConfig[]>([]);
  const [groups, setGroups] = useState<AjoAdminGroup[]>([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  const [feeDraft, setFeeDraft] = useState<Record<string, string>>({});

  const fetchAll = useCallback(async () => {
    try {
      setLoading(true);
      const [feesData, groupsData] = await Promise.all([
        ajoApi.getFees(),
        ajoApi.getGroups(),
      ]);
      setFees(feesData);
      setGroups(groupsData);
      setFeeDraft(
        feesData.reduce<Record<string, string>>((acc, fee) => {
          acc[fee.feeKey] = String(fee.amount);
          return acc;
        }, {}),
      );
    } catch (err) {
      toast.error(err, 'Failed to load Ajo data.');
    } finally {
      setLoading(false);
    }
  }, [toast]);

  useEffect(() => {
    void fetchAll();
  }, [fetchAll]);

  const saveFee = async (fee: AjoFeeConfig) => {
    const amount = Number(feeDraft[fee.feeKey]);
    if (Number.isNaN(amount) || amount < 0) {
      toast.warning('Enter a valid fee amount.');
      return;
    }
    try {
      setSaving(true);
      await ajoApi.updateFee(fee.feeKey, {
        amount,
        description: fee.description ?? undefined,
      });
      await fetchAll();
      toast.success(`${titleFromKey(fee.feeKey)} updated.`);
    } catch (err) {
      toast.error(err, 'Could not update Ajo fee.');
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div style={{ display: 'flex', justifyContent: 'center', padding: 48 }}>
        <Spinner size={24} />
      </div>
    );
  }

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 24 }}>
      <div
        style={{
          display: 'flex',
          gap: 2,
          borderBottom: '1px solid var(--neutral-300)',
          paddingBottom: 0,
          overflowX: 'auto',
        }}
      >
        {tabs.map((tab) => {
          const isActive = activeTab === tab.key;
          const Icon = tab.icon;
          return (
            <button
              key={tab.key}
              onClick={() => setActiveTab(tab.key)}
              style={{
                padding: '10px 20px',
                fontSize: 14,
                fontWeight: 500,
                fontFamily: 'var(--font-family)',
                color: isActive ? 'var(--blue-600)' : 'var(--neutral-600)',
                background: 'transparent',
                borderBottom: isActive ? '2px solid var(--blue-600)' : '2px solid transparent',
                marginBottom: -1,
                whiteSpace: 'nowrap',
                display: 'flex',
                alignItems: 'center',
                gap: 8,
              }}
            >
              <Icon size={16} />
              {tab.label}
            </button>
          );
        })}
      </div>

      {activeTab === 'groups' && <AjoGroupsTab groups={groups} />}
      {activeTab === 'wallet' && <AjoWalletTab canOperate={!!canEdit} canSetAccount={admin?.role === 'super_admin'} />}
      {activeTab === 'fees' && (
        <AjoFeesTab
          fees={fees}
          feeDraft={feeDraft}
          setFeeDraft={setFeeDraft}
          saveFee={saveFee}
          canEdit={!!canEdit}
          saving={saving}
        />
      )}
      {activeTab === 'risk' && <AjoRiskTab groups={groups} />}
      {activeTab === 'exits' && <AjoExitsTab canDecide={!!canEdit} />}
      {activeTab === 'listings' && <AjoListingsTab canDecide={!!canEdit} />}
      {activeTab === 'ops' && <AjoOpsTab groups={groups} canTriggerRetention={admin?.role === 'super_admin'} />}
    </div>
  );
};

function AjoGroupsTab({ groups }: { groups: AjoAdminGroup[] }) {
  const stats = useMemo(() => {
    const totalMembers = groups.reduce((sum, group) => sum + group.membersCount, 0);
    const pendingInvites = groups.reduce((sum, group) => sum + group.pendingInvitesCount, 0);
    const inviting = groups.filter((group) => group.status === 'inviting').length;
    const active = groups.filter((group) => group.status === 'active').length;
    const totalTarget = groups.reduce((sum, group) => sum + group.expectedParticipants, 0);
    const fillRate = totalTarget ? Math.round((totalMembers / totalTarget) * 100) : 0;
    return { totalMembers, pendingInvites, inviting, active, fillRate };
  }, [groups]);

  const statusData = useMemo(() => {
    const map = new Map<string, number>();
    groups.forEach((group) => map.set(group.status, (map.get(group.status) ?? 0) + 1));
    return Array.from(map.entries()).map(([status, count]) => ({ status, count }));
  }, [groups]);

  const intervalData = useMemo(() => {
    const map = new Map<string, number>();
    groups.forEach((group) => map.set(group.interval, (map.get(group.interval) ?? 0) + 1));
    return Array.from(map.entries()).map(([interval, count]) => ({ interval, count }));
  }, [groups]);

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 20 }}>
      <div
        style={{
          display: 'grid',
          gridTemplateColumns: 'repeat(auto-fit, minmax(min(100%, 190px), 1fr))',
          gap: 16,
        }}
      >
        <StatCard label="Total groups" value={groups.length} icon={HandCoins} accent="blue" />
        <StatCard label="Active groups" value={stats.active} icon={Users} accent="green" />
        <StatCard label="Inviting" value={stats.inviting} icon={Layers} accent="purple" />
        <StatCard label="Pending invites" value={stats.pendingInvites} icon={Receipt} accent="neutral" />
        <StatCard label="Fill rate" value={`${stats.fillRate}%`} icon={Users} accent="green" />
      </div>

      <div
        style={{
          display: 'grid',
          gridTemplateColumns: 'repeat(auto-fit, minmax(min(100%, 320px), 1fr))',
          gap: 16,
        }}
      >
        <Card>
          <h3 style={{ fontSize: 15, fontWeight: 700, color: 'var(--neutral-800)' }}>
            Groups by Status
          </h3>
          <p style={{ fontSize: 13, color: 'var(--neutral-500)', marginTop: 2 }}>
            Quick audit of group lifecycle spread.
          </p>
          <div style={{ height: 260, marginTop: 16 }}>
            <ResponsiveContainer width="100%" height="100%">
              <BarChart data={statusData} margin={{ top: 5, right: 10, left: 0, bottom: 0 }}>
                <CartesianGrid strokeDasharray="3 3" stroke="var(--neutral-300)" />
                <XAxis dataKey="status" tick={{ fill: 'var(--neutral-600)', fontSize: 12 }} />
                <YAxis allowDecimals={false} tick={{ fill: 'var(--neutral-600)', fontSize: 12 }} />
                <Tooltip />
                <Bar dataKey="count" fill="var(--blue-600)" radius={[6, 6, 0, 0]} />
              </BarChart>
            </ResponsiveContainer>
          </div>
        </Card>

        <Card>
          <h3 style={{ fontSize: 15, fontWeight: 700, color: 'var(--neutral-800)' }}>
            Groups by Interval
          </h3>
          <p style={{ fontSize: 13, color: 'var(--neutral-500)', marginTop: 2 }}>
            Contribution cadence mix for support checks.
          </p>
          <div style={{ height: 260, marginTop: 16 }}>
            <ResponsiveContainer width="100%" height="100%">
              <BarChart data={intervalData} margin={{ top: 5, right: 10, left: 0, bottom: 0 }}>
                <CartesianGrid strokeDasharray="3 3" stroke="var(--neutral-300)" />
                <XAxis dataKey="interval" tick={{ fill: 'var(--neutral-600)', fontSize: 12 }} />
                <YAxis allowDecimals={false} tick={{ fill: 'var(--neutral-600)', fontSize: 12 }} />
                <Tooltip />
                <Bar dataKey="count" fill="var(--green-600)" radius={[6, 6, 0, 0]} />
              </BarChart>
            </ResponsiveContainer>
          </div>
        </Card>
      </div>

      <Card padding={0}>
        <div style={{ padding: '16px 20px', borderBottom: '1px solid var(--neutral-300)' }}>
          <h3 style={{ fontSize: 15, fontWeight: 600, color: 'var(--neutral-800)' }}>
            Ajo Groups
          </h3>
          <p style={{ fontSize: 13, color: 'var(--neutral-500)', marginTop: 2 }}>
            Operational read-only view of created groups, members, and pending invites.
          </p>
        </div>
        <div style={{ overflowX: 'auto' }}>
          <table style={{ width: '100%', minWidth: 940, borderCollapse: 'collapse', fontSize: 13 }}>
            <thead>
              <tr style={{ background: 'var(--neutral-200)' }}>
                {['Group', 'Creator', 'Amount', 'Interval', 'Members', 'Pending', 'Start', 'Status'].map((header) => (
                  <th
                    key={header}
                    style={{
                      padding: '12px 14px',
                      textAlign: 'left',
                      fontSize: 12,
                      color: 'var(--neutral-600)',
                      textTransform: 'uppercase',
                      letterSpacing: 0.4,
                    }}
                  >
                    {header}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {groups.length === 0 ? (
                <tr>
                  <td colSpan={8} style={{ padding: 20, color: 'var(--neutral-500)' }}>
                    No Ajo groups have been created yet.
                  </td>
                </tr>
              ) : (
                groups.map((group) => (
                  <tr key={group.id} style={{ borderTop: '1px solid var(--neutral-200)' }}>
                    <td style={{ padding: '12px 14px' }}>
                      <div style={{ fontWeight: 600, color: 'var(--neutral-800)' }}>{group.name}</div>
                      <div style={{ fontSize: 12, color: 'var(--neutral-500)' }}>{group.inviteCode}</div>
                    </td>
                    <td style={{ padding: '12px 14px' }}>
                      <div style={{ fontWeight: 500, color: 'var(--neutral-800)' }}>
                        {group.creator?.name ?? 'Unknown'}
                      </div>
                      <div style={{ fontSize: 12, color: 'var(--neutral-500)' }}>
                        {group.creator?.email ?? group.creator?.phoneNumber ?? '—'}
                      </div>
                    </td>
                    <td style={{ padding: '12px 14px', fontFamily: 'monospace' }}>
                      {formatMoney(group.contributionAmount)}
                    </td>
                    <td style={{ padding: '12px 14px' }}>{group.interval}</td>
                    <td style={{ padding: '12px 14px' }}>
                      {group.membersCount}/{group.expectedParticipants}
                    </td>
                    <td style={{ padding: '12px 14px' }}>{group.pendingInvitesCount}</td>
                    <td style={{ padding: '12px 14px' }}>{formatDate(group.startDate)}</td>
                    <td style={{ padding: '12px 14px' }}>
                      <Badge variant={badgeVariant(group.status)}>{group.status}</Badge>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </Card>
    </div>
  );
}

function AjoFeesTab(props: {
  fees: AjoFeeConfig[];
  feeDraft: Record<string, string>;
  setFeeDraft: React.Dispatch<React.SetStateAction<Record<string, string>>>;
  saveFee: (fee: AjoFeeConfig) => void;
  canEdit: boolean;
  saving: boolean;
}) {
  const { fees, feeDraft, setFeeDraft, saveFee, canEdit, saving } = props;
  return (
    <Card padding={0}>
      <div style={{ padding: '16px 20px', borderBottom: '1px solid var(--neutral-300)' }}>
        <h3 style={{ fontSize: 15, fontWeight: 600, color: 'var(--neutral-800)' }}>
          Ajo Fees
        </h3>
        <p style={{ fontSize: 13, color: 'var(--neutral-500)', marginTop: 2 }}>
          Fee constants used by the upcoming Ajo workflows.
        </p>
      </div>
      {fees.map((fee, index) => (
        <div
          key={fee.feeKey}
          style={{
            display: 'grid',
            gridTemplateColumns: 'minmax(240px, 1fr) 160px auto',
            gap: 16,
            alignItems: 'center',
            padding: '14px 20px',
            borderTop: index === 0 ? 'none' : '1px solid var(--neutral-200)',
            background: index % 2 === 0 ? 'var(--neutral-100)' : 'var(--neutral-200)',
          }}
        >
          <div>
            <div style={{ fontSize: 14, fontWeight: 600, color: 'var(--neutral-800)' }}>
              {titleFromKey(fee.feeKey)}
            </div>
            <div style={{ fontSize: 12, color: 'var(--neutral-500)', marginTop: 2 }}>
              {fee.description}
            </div>
          </div>
          <input
            style={inputStyle}
            type="number"
            min={0}
            disabled={!canEdit}
            value={feeDraft[fee.feeKey] ?? ''}
            onChange={(e) =>
              setFeeDraft((current) => ({
                ...current,
                [fee.feeKey]: e.target.value,
              }))
            }
          />
          {canEdit && (
            <Button size="sm" variant="secondary" onClick={() => saveFee(fee)} loading={saving}>
              Save
            </Button>
          )}
        </div>
      ))}
    </Card>
  );
}

// ── New tabs (Phase 4–10 endpoints) ────────────────────────────────────────

function AjoRiskTab({ groups }: { groups: AjoAdminGroup[] }) {
  const toast = useToast();
  const [atRisk, setAtRisk] = useState<AjoGroupHealth[]>([]);
  const [loading, setLoading] = useState(true);
  const [selectedGroupId, setSelectedGroupId] = useState('');
  const [groupHealth, setGroupHealth] = useState<AjoGroupHealth | null>(null);
  const [healthLoading, setHealthLoading] = useState(false);

  useEffect(() => {
    void (async () => {
      try {
        setLoading(true);
        const data = await ajoApi.getGroupsAtRisk();
        setAtRisk(data);
      } catch (err) {
        toast.error(err, 'Failed to load at-risk groups.');
      } finally {
        setLoading(false);
      }
    })();
  }, [toast]);

  const loadGroupHealth = useCallback(
    async (id: string) => {
      if (!id) {
        setGroupHealth(null);
        return;
      }
      try {
        setHealthLoading(true);
        const data = await ajoApi.getGroupHealth(id);
        setGroupHealth(data);
      } catch (err) {
        toast.error(err, 'Failed to load group health.');
      } finally {
        setHealthLoading(false);
      }
    },
    [toast],
  );

  useEffect(() => {
    void loadGroupHealth(selectedGroupId);
  }, [selectedGroupId, loadGroupHealth]);

  if (loading) return <Spinner size={20} />;

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 20 }}>
      <Card>
        <h3 style={{ fontSize: 15, fontWeight: 700, color: 'var(--neutral-800)' }}>
          Check Group Health
        </h3>
        <p style={{ fontSize: 13, color: 'var(--neutral-500)', marginTop: 2 }}>
          Select a group to view its health score, factors, and risk flags.
        </p>
        <select
          value={selectedGroupId}
          onChange={(e) => setSelectedGroupId(e.target.value)}
          style={{ ...inputStyle, marginTop: 12, maxWidth: 360 }}
        >
          <option value="">— Select a group —</option>
          {groups.map((g) => (
            <option key={g.id} value={g.id}>
              {g.name}
            </option>
          ))}
        </select>

        {healthLoading && (
          <div style={{ marginTop: 16 }}>
            <Spinner size={16} />
          </div>
        )}

        {groupHealth && !healthLoading && (
          <div style={{ marginTop: 16, display: 'flex', flexDirection: 'column', gap: 12 }}>
            <div style={{ display: 'flex', gap: 12, alignItems: 'center' }}>
              <span style={{ fontSize: 32, fontWeight: 700, color: 'var(--blue-600)' }}>
                {groupHealth.score}
              </span>
              <Badge variant={badgeVariant(groupHealth.grade === 'A' || groupHealth.grade === 'B' ? 'active' : groupHealth.grade === 'C' ? 'inviting' : 'cancelled')}>
                Grade {groupHealth.grade}
              </Badge>
            </div>
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(min(100%, 180px), 1fr))', gap: 12 }}>
              <div><strong>Payment timeliness:</strong> {groupHealth.factors.paymentTimeliness}%</div>
              <div><strong>Member retention:</strong> {groupHealth.factors.memberRetention}%</div>
              <div><strong>Default rate:</strong> {groupHealth.factors.defaultRate}%</div>
              <div><strong>Completion:</strong> {groupHealth.factors.completionProgress}%</div>
            </div>
            {groupHealth.riskFlags.length > 0 && (
              <div>
                <h4 style={{ fontSize: 13, fontWeight: 700, color: 'var(--neutral-800)' }}>
                  Risk flags
                </h4>
                <ul style={{ marginTop: 6, paddingLeft: 20, color: 'var(--neutral-700)', fontSize: 13 }}>
                  {groupHealth.riskFlags.map((f) => (
                    <li key={f}>{f}</li>
                  ))}
                </ul>
              </div>
            )}
          </div>
        )}
      </Card>

      <Card>
        <h3 style={{ fontSize: 15, fontWeight: 700, color: 'var(--neutral-800)' }}>
          Groups at Risk (score &lt; 60)
        </h3>
        <p style={{ fontSize: 13, color: 'var(--neutral-500)', marginTop: 2 }}>
          {atRisk.length} group(s) require attention.
        </p>
        {atRisk.length === 0 ? (
          <p style={{ marginTop: 12, color: 'var(--neutral-500)' }}>No at-risk groups.</p>
        ) : (
          <div style={{ marginTop: 12, display: 'flex', flexDirection: 'column', gap: 8 }}>
            {atRisk.map((h) => (
              <div
                key={h.groupId}
                style={{
                  padding: 12,
                  border: '1px solid var(--neutral-300)',
                  borderRadius: 'var(--radius-md)',
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'center',
                  gap: 12,
                }}
              >
                <div>
                  <div style={{ fontWeight: 600 }}>{h.groupName}</div>
                  <div style={{ fontSize: 12, color: 'var(--neutral-500)' }}>
                    Score {h.score} • {h.riskFlags.length} flag(s)
                  </div>
                </div>
                <Badge variant="danger">Grade {h.grade}</Badge>
              </div>
            ))}
          </div>
        )}
      </Card>
    </div>
  );
}

function AjoOpsTab({ groups, canTriggerRetention }: { groups: AjoAdminGroup[]; canTriggerRetention: boolean }) {
  const toast = useToast();
  const [referralStats, setReferralStats] = useState<AjoReferralStats | null>(null);
  const [loading, setLoading] = useState(true);
  const [retentionRunning, setRetentionRunning] = useState(false);
  const [exportGroupId, setExportGroupId] = useState('');
  const [exporting, setExporting] = useState(false);

  useEffect(() => {
    void (async () => {
      try {
        setLoading(true);
        const data = await ajoApi.getReferralStats();
        setReferralStats(data);
      } catch (err) {
        toast.error(err, 'Failed to load referral stats.');
      } finally {
        setLoading(false);
      }
    })();
  }, [toast]);

  const runRetention = async () => {
    if (!window.confirm('Run the data retention cron now? This will scrub guest data on completed groups past the retention windows.')) {
      return;
    }
    try {
      setRetentionRunning(true);
      await ajoApi.triggerRetention();
      toast.success('Retention cron triggered.');
    } catch (err) {
      toast.error(err, 'Failed to trigger retention.');
    } finally {
      setRetentionRunning(false);
    }
  };

  const runExport = async () => {
    if (!exportGroupId) return;
    try {
      setExporting(true);
      const data = await ajoApi.exportGroup(exportGroupId);
      const blob = new Blob([JSON.stringify(data, null, 2)], { type: 'application/json' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `ajo-group-${exportGroupId}.json`;
      a.click();
      URL.revokeObjectURL(url);
      toast.success('Export downloaded.');
    } catch (err) {
      toast.error(err, 'Failed to export group.');
    } finally {
      setExporting(false);
    }
  };

  if (loading) return <Spinner size={20} />;

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 20 }}>
      <Card>
        <h3 style={{ fontSize: 15, fontWeight: 700, color: 'var(--neutral-800)' }}>
          Group History Export
        </h3>
        <p style={{ fontSize: 13, color: 'var(--neutral-500)', marginTop: 2 }}>
          Download a full JSON snapshot of a group's members, rounds, and contributions.
        </p>
        <div style={{ display: 'flex', gap: 12, marginTop: 12, flexWrap: 'wrap' }}>
          <select
            value={exportGroupId}
            onChange={(e) => setExportGroupId(e.target.value)}
            style={{ ...inputStyle, maxWidth: 360 }}
          >
            <option value="">— Select a group —</option>
            {groups.map((g) => (
              <option key={g.id} value={g.id}>
                {g.name}
              </option>
            ))}
          </select>
          <Button onClick={runExport} disabled={!exportGroupId || exporting}>
            <Download size={14} style={{ marginRight: 6 }} />
            {exporting ? 'Exporting…' : 'Export JSON'}
          </Button>
        </div>
      </Card>

      <Card>
        <h3 style={{ fontSize: 15, fontWeight: 700, color: 'var(--neutral-800)' }}>
          Data Retention
        </h3>
        <p style={{ fontSize: 13, color: 'var(--neutral-500)', marginTop: 2 }}>
          The cron runs nightly at 03:00 WAT. Use this only to trigger an out-of-band run.
        </p>
        <div style={{ marginTop: 12 }}>
          <Button
            onClick={runRetention}
            disabled={!canTriggerRetention || retentionRunning}
            variant="secondary"
          >
            {retentionRunning ? 'Running…' : 'Run retention now'}
          </Button>
          {!canTriggerRetention && (
            <span style={{ marginLeft: 12, fontSize: 12, color: 'var(--neutral-500)' }}>
              Super admin only.
            </span>
          )}
        </div>
      </Card>

      <Card>
        <h3 style={{ fontSize: 15, fontWeight: 700, color: 'var(--neutral-800)' }}>
          Top Ajo Referral Creators
        </h3>
        <p style={{ fontSize: 13, color: 'var(--neutral-500)', marginTop: 2 }}>
          {referralStats?.totalAjoReferrals ?? 0} accepted Ajo-driven referrals total.
        </p>
        {!referralStats?.topCreators.length ? (
          <p style={{ marginTop: 12, color: 'var(--neutral-500)' }}>No referral data yet.</p>
        ) : (
          <div style={{ marginTop: 12, display: 'flex', flexDirection: 'column', gap: 8 }}>
            {referralStats.topCreators.map((entry) => (
              <div
                key={entry.creatorId}
                style={{
                  padding: 12,
                  border: '1px solid var(--neutral-300)',
                  borderRadius: 'var(--radius-md)',
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'center',
                  gap: 12,
                }}
              >
                <div>
                  <div style={{ fontWeight: 600 }}>{entry.creatorName ?? 'Unknown'}</div>
                  <div style={{ fontSize: 12, color: 'var(--neutral-500)' }}>
                    {entry.creatorEmail ?? '—'}
                  </div>
                </div>
                <Badge variant="success">{entry.acceptedInvites} accepted</Badge>
              </div>
            ))}
          </div>
        )}
      </Card>
    </div>
  );
}

/**
 * Organisers ask for a member to leave a running group — they cannot
 * continue, or they have died. Nothing changes until this is approved, and
 * approving is maker-checker: it moves members' money.
 *
 * For a death, check the documents the organiser sent support before
 * approving. An organiser who could remove somebody by reporting them dead
 * is the thing this queue exists to stop.
 */
function AjoExitsTab({ canDecide }: { canDecide: boolean }) {
  const toast = useToast();
  const [rows, setRows] = useState<AjoMemberExitRequest[]>([]);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState<string | null>(null);

  const load = useCallback(async () => {
    try {
      setLoading(true);
      setRows(await ajoApi.getPendingExits());
    } catch (err) {
      toast.error(err, 'Failed to load exit requests.');
    } finally {
      setLoading(false);
    }
  }, [toast]);

  useEffect(() => {
    void load();
  }, [load]);

  const approve = async (row: AjoMemberExitRequest) => {
    if (!window.confirm(`Approve ${row.memberName ?? 'this member'} leaving ${row.groupName ?? 'the group'}? This moves money.`)) return;
    try {
      setBusy(row.id);
      const result = await ajoApi.approveExit(row.id);
      toast.success(
        isApprovalRequired(result)
          ? 'Sent for a second admin to approve.'
          : 'Exit approved and carried out.',
      );
      await load();
    } catch (err) {
      toast.error(err, 'Could not approve that exit.');
    } finally {
      setBusy(null);
    }
  };

  const reject = async (row: AjoMemberExitRequest) => {
    const note = window.prompt('Why is it not approved? The organiser is told.');
    if (!note?.trim()) return;
    try {
      setBusy(row.id);
      await ajoApi.rejectExit(row.id, note.trim());
      toast.success('Exit request rejected.');
      await load();
    } catch (err) {
      toast.error(err, 'Could not reject that exit.');
    } finally {
      setBusy(null);
    }
  };

  if (loading) {
    return (
      <div style={{ display: 'flex', justifyContent: 'center', padding: 48 }}>
        <Spinner size={24} />
      </div>
    );
  }

  return (
    <Card>
      {rows.length === 0 ? (
        <div style={{ padding: 24, color: 'var(--neutral-600)', fontSize: 14 }}>
          No exit requests waiting.
        </div>
      ) : (
        <div style={{ overflowX: 'auto' }}>
          <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 14 }}>
            <thead>
              <tr style={{ textAlign: 'left', color: 'var(--neutral-600)' }}>
                {['Member', 'Group', 'Reason', 'What it comes to', 'Asked by', 'Asked', ''].map((h) => (
                  <th key={h} style={{ padding: '10px 14px', fontWeight: 500 }}>{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {rows.map((r) => (
                <tr key={r.id} style={{ borderTop: '1px solid var(--neutral-200)' }}>
                  <td style={{ padding: '12px 14px' }}>{r.memberName ?? '—'}</td>
                  <td style={{ padding: '12px 14px' }}>{r.groupName ?? '—'}</td>
                  <td style={{ padding: '12px 14px' }}>
                    <Badge variant={r.reason === 'deceased' ? 'danger' : 'warning'}>
                      {r.reason === 'deceased' ? 'Deceased' : 'Cannot continue'}
                    </Badge>
                    {r.note ? (
                      <div style={{ marginTop: 6, fontSize: 12, color: 'var(--neutral-600)' }}>{r.note}</div>
                    ) : null}
                  </td>
                  <td style={{ padding: '12px 14px' }}>
                    {r.mode === 'refund'
                      ? `Refund ${formatMoney(r.refundDue)} from their own turn`
                      : `${formatMoney(r.debtTotal)} owed — ${formatMoney(r.sharePerParty)} each; organiser pays ${formatMoney(r.organiserShare)} now`}
                  </td>
                  <td style={{ padding: '12px 14px' }}>{r.organiserName ?? '—'}</td>
                  <td style={{ padding: '12px 14px' }}>{formatDate(r.createdAt)}</td>
                  <td style={{ padding: '12px 14px', whiteSpace: 'nowrap' }}>
                    {canDecide ? (
                      <div style={{ display: 'flex', gap: 8 }}>
                        <Button onClick={() => void approve(r)} disabled={busy === r.id}>Approve</Button>
                        <Button variant="secondary" onClick={() => void reject(r)} disabled={busy === r.id}>
                          Reject
                        </Button>
                      </div>
                    ) : null}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </Card>
  );
}

const LISTING_VIEWS: { key: 'pending_review' | 'listed' | 'unpublished'; label: string }[] = [
  { key: 'pending_review', label: 'Held for review' },
  { key: 'listed', label: 'Live' },
  { key: 'unpublished', label: 'Taken down' },
];

const REPORT_REASONS: Record<string, string> = {
  scam: 'Looks like a scam',
  misleading: 'Misleading',
  offensive: 'Offensive',
  other: 'Other',
};

function requirementWords(r: AjoListingForReview['requirements']): string {
  if (!r) return 'PayIQ defaults only';
  const parts: string[] = [];
  if (r.minTier) parts.push(`Tier ${r.minTier}+`);
  if (r.minReliability) parts.push(`score ${r.minReliability}+`);
  if (r.states?.length) parts.push(r.states.join(', '));
  if (r.emailDomain) parts.push(`@${r.emailDomain}`);
  if (r.questions?.length) parts.push(`${r.questions.length} question${r.questions.length === 1 ? '' : 's'}`);
  return parts.join(' · ') || 'PayIQ defaults only';
}

/**
 * Public Ajo listings — groups strangers can find and ask to join.
 *
 * A listing is held here when its own words look wrong (links, phone or
 * account numbers, promised returns, moving people off PayIQ), when enough
 * people report it, or when PayIQ has chosen to review every listing
 * (`public_review_all_listings` in Ajo Fees). Approving puts it in the
 * directory. Taking it down tells the organiser the reason, closes every
 * open join request, and only PayIQ can list it again.
 *
 * No money moves here, so there is no second approver.
 */
function AjoListingsTab({ canDecide }: { canDecide: boolean }) {
  const toast = useToast();
  const [view, setView] = useState<'pending_review' | 'listed' | 'unpublished'>('pending_review');
  const [rows, setRows] = useState<AjoListingForReview[]>([]);
  const [reports, setReports] = useState<AjoListingReportRow[]>([]);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState<string | null>(null);

  const load = useCallback(async () => {
    try {
      setLoading(true);
      const [listings, open] = await Promise.all([
        ajoApi.getListings(view),
        ajoApi.getListingReports(),
      ]);
      setRows(listings);
      setReports(open);
    } catch (err) {
      toast.error(err, 'Failed to load listings.');
    } finally {
      setLoading(false);
    }
  }, [toast, view]);

  useEffect(() => {
    void load();
  }, [load]);

  const approve = async (row: AjoListingForReview) => {
    if (!window.confirm(`Put "${row.name}" in the Ajo directory?`)) return;
    try {
      setBusy(row.id);
      await ajoApi.approveListing(row.id);
      toast.success('Listing is live.');
      await load();
    } catch (err) {
      toast.error(err, 'Could not approve that listing.');
    } finally {
      setBusy(null);
    }
  };

  const unpublish = async (row: AjoListingForReview) => {
    const reason = window.prompt(
      'Why is it coming down? The organiser sees this, so keep it factual and free of compliance language.',
    );
    if (!reason?.trim()) return;
    try {
      setBusy(row.id);
      await ajoApi.unpublishListing(row.id, reason.trim());
      toast.success('Listing taken down. Open join requests were closed.');
      await load();
    } catch (err) {
      toast.error(err, 'Could not take that listing down.');
    } finally {
      setBusy(null);
    }
  };

  const dismiss = async (report: AjoListingReportRow) => {
    try {
      setBusy(report.id);
      await ajoApi.dismissListingReport(report.id);
      toast.success('Report dismissed.');
      await load();
    } catch (err) {
      toast.error(err, 'Could not dismiss that report.');
    } finally {
      setBusy(null);
    }
  };

  const cell: React.CSSProperties = { padding: '12px 14px', verticalAlign: 'top' };
  const muted: React.CSSProperties = { marginTop: 4, fontSize: 12, color: 'var(--neutral-600)' };

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
      <div style={{ display: 'flex', gap: 8 }}>
        {LISTING_VIEWS.map((v) => (
          <Button key={v.key} variant={view === v.key ? 'primary' : 'secondary'} onClick={() => setView(v.key)}>
            {v.label}
          </Button>
        ))}
      </div>

      <Card>
        {loading ? (
          <div style={{ display: 'flex', justifyContent: 'center', padding: 48 }}>
            <Spinner size={24} />
          </div>
        ) : rows.length === 0 ? (
          <div style={{ padding: 24, color: 'var(--neutral-600)', fontSize: 14 }}>
            {view === 'pending_review' ? 'No listings waiting for review.' : 'Nothing here.'}
          </div>
        ) : (
          <div style={{ overflowX: 'auto' }}>
            <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 14 }}>
              <thead>
                <tr style={{ textAlign: 'left', color: 'var(--neutral-600)' }}>
                  {['Group', 'Terms', 'Organiser', 'Why it is here', 'Requirements', ''].map((h) => (
                    <th key={h} style={{ padding: '10px 14px', fontWeight: 500 }}>{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {rows.map((r) => (
                  <tr key={r.id} style={{ borderTop: '1px solid var(--neutral-200)' }}>
                    <td style={{ ...cell, maxWidth: 320 }}>
                      <div style={{ fontWeight: 500 }}>{r.name}</div>
                      {r.description ? <div style={muted}>{r.description}</div> : null}
                      <div style={muted}>
                        {[r.category, r.state].filter(Boolean).join(' · ')}
                        {r.status !== 'forming' ? ` · ${r.status}` : ''}
                      </div>
                    </td>
                    <td style={cell}>
                      {formatMoney(r.contributionAmount)} {r.interval}
                      <div style={muted}>
                        {r.expectedParticipants} people · {r.joinMode === 'automatic' ? 'anyone eligible joins' : 'organiser approves'}
                      </div>
                    </td>
                    <td style={cell}>
                      {r.organiser.name}
                      <div style={muted}>
                        Tier {r.organiser.tier} · {r.organiser.cleanGroupsCompleted}/{r.organiser.groupsCompleted} finished clean ·{' '}
                        {r.organiser.groupsRunning} running
                      </div>
                    </td>
                    <td style={cell}>
                      {r.flags.length === 0 && r.reports.length === 0 ? (
                        <span style={{ color: 'var(--neutral-600)' }}>
                          {view === 'pending_review' ? 'Review of all listings is on' : '—'}
                        </span>
                      ) : null}
                      <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>
                        {r.flags.map((f) => (
                          <Badge key={f} variant="warning">{f}</Badge>
                        ))}
                        {r.reports.length ? (
                          <Badge variant="danger">
                            {r.reports.length} report{r.reports.length === 1 ? '' : 's'}
                          </Badge>
                        ) : null}
                      </div>
                      {r.reports.slice(0, 3).map((rep) => (
                        <div key={rep.id} style={muted}>
                          {REPORT_REASONS[rep.reason] ?? rep.reason}
                          {rep.note ? `: ${rep.note}` : ''}
                        </div>
                      ))}
                      {view === 'unpublished' && r.listingNote ? <div style={muted}>{r.listingNote}</div> : null}
                    </td>
                    <td style={{ ...cell, maxWidth: 240 }}>
                      {requirementWords(r.requirements)}
                      {r.requirements?.questions?.map((qn) => (
                        <div key={qn} style={muted}>“{qn}”</div>
                      ))}
                    </td>
                    <td style={{ ...cell, whiteSpace: 'nowrap' }}>
                      {canDecide ? (
                        <div style={{ display: 'flex', gap: 8 }}>
                          {view !== 'listed' && r.status === 'forming' ? (
                            <Button onClick={() => void approve(r)} disabled={busy === r.id}>
                              {view === 'unpublished' ? 'Relist' : 'Approve'}
                            </Button>
                          ) : null}
                          {view !== 'unpublished' ? (
                            <Button variant="danger" onClick={() => void unpublish(r)} disabled={busy === r.id}>
                              Take down
                            </Button>
                          ) : null}
                        </div>
                      ) : null}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </Card>

      <CoverPoolCard />
      <RatingsCard canDecide={canDecide} />

      <Card>
        <div style={{ padding: '14px 14px 0', fontWeight: 500 }}>Open reports</div>
        {reports.length === 0 ? (
          <div style={{ padding: 14, color: 'var(--neutral-600)', fontSize: 14 }}>No open reports.</div>
        ) : (
          <div style={{ overflowX: 'auto' }}>
            <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 14 }}>
              <tbody>
                {reports.map((rep) => (
                  <tr key={rep.id} style={{ borderTop: '1px solid var(--neutral-200)' }}>
                    <td style={cell}>{rep.group?.name ?? '—'}</td>
                    <td style={cell}>
                      {REPORT_REASONS[rep.reason] ?? rep.reason}
                      {rep.note ? <div style={muted}>{rep.note}</div> : null}
                    </td>
                    <td style={cell}>{formatDate(rep.createdAt)}</td>
                    <td style={{ ...cell, whiteSpace: 'nowrap' }}>
                      {canDecide ? (
                        <Button variant="secondary" onClick={() => void dismiss(rep)} disabled={busy === rep.id}>
                          Dismiss
                        </Button>
                      ) : null}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </Card>
    </div>
  );
}

/**
 * The cover pool: public payouts pay a levy in, overdue shares are paid out
 * of it, and members pay it back. Off until AJO_COVER_POOL_ENABLED and
 * AJO_COVER_POOL_ACCOUNT are set; the levy is `public_cover_levy_pct` in Ajo
 * Fees. Needs legal sign-off before it is switched on.
 */
function CoverPoolCard() {
  const [pool, setPool] = useState<AjoCoverPoolSummary | null>(null);
  useEffect(() => {
    ajoApi.getCoverPool().then(setPool).catch(() => setPool(null));
  }, []);
  if (!pool) return null;
  const cells: Array<[string, string]> = [
    ['Holds', formatMoney(pool.balance)],
    ['Levied in', formatMoney(pool.levied)],
    ['Covered', formatMoney(pool.covered)],
    ['Paid back', formatMoney(pool.recovered)],
    ['Still owed', formatMoney(pool.owing)],
  ];
  return (
    <Card>
      <div style={{ padding: 14, display: 'flex', flexDirection: 'column', gap: 12 }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
          <span style={{ fontWeight: 500 }}>Cover pool</span>
          <Badge variant={pool.enabled ? 'success' : 'neutral'}>{pool.enabled ? 'On' : 'Off'}</Badge>
          <span style={{ fontSize: 12, color: 'var(--neutral-600)' }}>Levy {pool.levyPct}% of each public payout</span>
        </div>
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(120px, 1fr))', gap: 12 }}>
          {cells.map(([label, value]) => (
            <div key={label}>
              <div style={{ fontSize: 12, color: 'var(--neutral-600)' }}>{label}</div>
              <div style={{ fontSize: 18, fontWeight: 600 }}>{value}</div>
            </div>
          ))}
        </div>
      </div>
    </Card>
  );
}

/**
 * What members said about organisers after a group ended. Shown on the
 * organiser's profile and listings; hiding one removes it from both and from
 * the average. The organiser is not told.
 */
function RatingsCard({ canDecide }: { canDecide: boolean }) {
  const toast = useToast();
  const [rows, setRows] = useState<AjoRatingRow[]>([]);
  const [showHidden, setShowHidden] = useState(false);
  const load = useCallback(async () => {
    try {
      setRows(await ajoApi.getRatings(showHidden));
    } catch (err) {
      toast.error(err, 'Failed to load ratings.');
    }
  }, [toast, showHidden]);
  useEffect(() => {
    void load();
  }, [load]);

  const hide = async (row: AjoRatingRow) => {
    const reason = window.prompt('Why is this rating being hidden? (kept for the record)');
    if (!reason?.trim()) return;
    try {
      await ajoApi.hideRating(row.id, reason.trim());
      toast.success('Rating hidden.');
      await load();
    } catch (err) {
      toast.error(err, 'Could not hide that rating.');
    }
  };

  const cell: React.CSSProperties = { padding: '12px 14px', verticalAlign: 'top' };
  return (
    <Card>
      <div style={{ padding: '14px 14px 0', display: 'flex', alignItems: 'center', gap: 12 }}>
        <span style={{ fontWeight: 500 }}>Organiser ratings</span>
        <label style={{ fontSize: 13, color: 'var(--neutral-600)', display: 'flex', gap: 6, alignItems: 'center' }}>
          <input type="checkbox" checked={showHidden} onChange={(e) => setShowHidden(e.target.checked)} />
          Include hidden
        </label>
      </div>
      {rows.length === 0 ? (
        <div style={{ padding: 14, color: 'var(--neutral-600)', fontSize: 14 }}>No ratings yet.</div>
      ) : (
        <div style={{ overflowX: 'auto' }}>
          <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 14 }}>
            <tbody>
              {rows.map((r) => (
                <tr key={r.id} style={{ borderTop: '1px solid var(--neutral-200)', opacity: r.hiddenAt ? 0.55 : 1 }}>
                  <td style={cell}>{'★'.repeat(r.stars)}{'☆'.repeat(5 - r.stars)}</td>
                  <td style={{ ...cell, maxWidth: 360 }}>
                    {r.comment ?? <span style={{ color: 'var(--neutral-600)' }}>No comment</span>}
                    {r.hiddenReason ? (
                      <div style={{ marginTop: 4, fontSize: 12, color: 'var(--neutral-600)' }}>Hidden: {r.hiddenReason}</div>
                    ) : null}
                  </td>
                  <td style={cell}>
                    {r.organiser}
                    <div style={{ marginTop: 4, fontSize: 12, color: 'var(--neutral-600)' }}>
                      by {r.rater}{r.group ? ` · ${r.group}` : ''}
                    </div>
                  </td>
                  <td style={cell}>{formatDate(r.createdAt)}</td>
                  <td style={{ ...cell, whiteSpace: 'nowrap' }}>
                    {canDecide && !r.hiddenAt ? (
                      <Button variant="secondary" onClick={() => void hide(r)}>Hide</Button>
                    ) : null}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </Card>
  );
}
